import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Ion } from "@/components/Ion";
import { useTheme } from "@/lib/theme";

const RED = "#E23E3E", YELLOW = "#F5A623", GREEN = "#58CD36";
const HEART_COLORS = [RED, YELLOW, GREEN];
const BEAT = [1.28, 1.0, 1.16, 1.0, 1.0, 1.0, 1.0, 1.0];

// Port of HealthCheckHero.tsx — beating heart + live month-to-date score.
export const HealthCheckHero = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [month, setMonth] = useState(null);
  const [scale, setScale] = useState(1);
  const [colorIdx, setColorIdx] = useState(0);
  const step = useRef(0);

  useEffect(() => {
    const load = () => api("/health-check/month").then(setMonth).catch(() => {});
    load();
    const onVis = () => document.visibilityState === "visible" && load();
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("focus", load); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    const beat = setInterval(() => { step.current = (step.current + 1) % BEAT.length; setScale(BEAT[step.current]); }, 130);
    const color = setInterval(() => setColorIdx((i) => (i + 1) % HEART_COLORS.length), 1600);
    return () => { clearInterval(beat); clearInterval(color); };
  }, []);

  const score = month ? Math.round(month.score) : null;
  const days = month?.days_elapsed ?? 0;
  const tint = score == null ? theme.textSecondary : score >= 70 ? GREEN : score >= 40 ? YELLOW : RED;
  const chip = score == null ? "GO" : score >= 70 ? "ON TRACK" : score >= 40 ? "GROWING" : "IMPROVE";

  return (
    <button onClick={() => navigate("/health-check")} data-testid="dashboard-health-hero"
      className="mb-3.5 flex w-full items-center rounded-2xl border border-[#252525] bg-[#0F0F0F] p-3.5 text-left active:opacity-85"
      style={{ boxShadow: "0 0 22px rgba(226, 62, 62, 0.15)" }}>
      <span className="mr-3.5 flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] bg-[#1A1A1A]">
        <span style={{ transform: `scale(${scale})` }} className="block">
          <span className="block text-center text-[34px] font-black leading-10" style={{ color: HEART_COLORS[colorIdx] }} data-testid="health-hero-heart">♥</span>
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="mb-0.5 block text-[10px] font-extrabold tracking-[1.6px] text-[#9AA0A6]">FRANCHISE LOCATION HEALTH CHECK</span>
        <span className="block truncate text-[26px] font-black leading-[30px] tracking-[-0.5px] text-white" data-testid="health-hero-score">
          {score != null ? `${score}` : "TAP TO START"}{score != null && <span className="text-[15px] font-bold text-[#7A7A7A]"> / 100</span>}
        </span>
        <span className="mt-0.5 block truncate text-xs text-[#B0B0B0]">{month?.month_label ?? "This month"} · {month?.active_days ?? 0} of {days} day{days !== 1 ? "s" : ""} active</span>
      </span>
      <span className="ml-2 flex h-14 flex-col items-end justify-between py-1">
        <span className="rounded-md px-2 py-[3px] text-[10px] font-black tracking-[1.3px]" style={{ border: `1.5px solid ${tint}`, color: tint }} data-testid="health-hero-chip">{chip}</span>
        <Ion name="chevron-forward" size={22} color={theme.text} />
      </span>
    </button>
  );
};
